/** Pure template-string renderers for credit-transaction ledger and danger zone.
 *
 * Layer: `view` per docs/design/spa-account-extraction-260524.md §3.2.
 *
 * File name follows the Phase AC.2 scaffold list; actual content is the
 * transaction-ledger view + danger-zone as directed by the design doc.
 *
 * Covers:
 *   - renderTokenTxTable   — credit-activity ledger table
 *   - renderDangerZone     — delete-account CTA section
 *
 * Purity contract: every exported function accepts typed data and returns a
 * string. No DOM access, no fetch, no window.X mutations.
 *
 * Extracted from web/static/account.html in Phase AC.4.
 */
import { fmtDate } from './view-usage.js';
const DEFAULT_MULTIPLIERS = {
    pdfCreditsPerPage: 2,
    officeCreditsPerPage: 1,
};
export function renderTokenTxTable(esc, transactions, pricing) {
    const P = pricing || DEFAULT_MULTIPLIERS;
    const rows = transactions.map(t => {
        const dt = fmtDate(t.created_at);
        const deltaCls = (t.delta != null && t.delta >= 0) ? 'tx-delta-plus' : 'tx-delta-minus';
        const delta = (t.delta != null && t.delta >= 0)
            ? ('+' + t.delta)
            : (t.delta == null ? '—' : String(t.delta));
        let detail = esc(t.reason || '—');
        if (t.format && typeof t.pages === 'number' && t.delta != null && t.delta < 0) {
            const fmtLabel = (t.format === 'pdf') ? 'PDF' : 'Office';
            const mult = (t.format === 'pdf') ? P.pdfCreditsPerPage : P.officeCreditsPerPage;
            detail = `${esc(fmtLabel)}, ${esc(t.pages)} page${t.pages === 1 ? '' : 's'} × ${mult} = ${Math.abs(t.delta)} credits`;
            if (t.reason && t.reason !== 'remediate') {
                detail += ` <span style="color:var(--text-muted)">(${esc(t.reason)})</span>`;
            }
        }
        const jobId = t.job_id
            ? '<a href="/history" title="Open history">' + esc(String(t.job_id).slice(0, 8)) + '…</a>'
            : '—';
        return `<tr>
            <td style="color:var(--text-muted)">${esc(dt)}</td>
            <td class="num ${deltaCls}">${esc(delta)}</td>
            <td class="tx-reason">${detail}</td>
            <td class="tx-job">${jobId}</td>
        </tr>`;
    }).join('');
    return `<div class="tx-table-wrap">
        <table class="tx-table" aria-label="Recent credit activity">
            <thead><tr>
                <th scope="col">Date</th>
                <th scope="col" class="num">Credits</th>
                <th scope="col">Detail</th>
                <th scope="col">Job</th>
            </tr></thead>
            <tbody>${rows}</tbody>
        </table>
    </div>`;
}
export function renderDangerZone() {
    return `<section class="account-section danger-zone-section" aria-labelledby="danger-h">
        <h2 id="danger-h" style="color:var(--error)">Danger Zone</h2>
        <p style="font-size:13px;color:var(--text-muted);margin-bottom:12px">
            Permanently delete your account. Your individual subscription will be cancelled
            and you will be removed from any group. Files will be cleaned up within 30 days.
        </p>
        <p style="font-size:12px;color:var(--text-muted);margin-bottom:12px">
            <strong>Note:</strong> If you are an admin of any active group subscriptions,
            you must cancel them before deleting your account.
        </p>
        <button class="account-btn danger" data-action="delete-account"
                aria-label="Permanently delete account — this cannot be undone">Delete my account</button>
    </section>`;
}
